import { useEffect, useState } from 'react';
import { X } from 'react-bootstrap-icons';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { getEventById, updateEvent } from '../../../http/eventAPI';
import Alert from 'react-bootstrap/Alert';
import { IEventResponseModel } from '../../../interfaces/responses/IEventResponseModel';

interface EventModalProps {
    show: boolean;
    handleClose: () => void;
    onEventEdited: () => void;
    eventId: string;
}

const EditEvent: React.FC<EventModalProps> = ({ show, handleClose, onEventEdited, eventId }) => {
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
	const [dateInputs, setDateInputs] = useState<string[]>([]);
    const [eventData, setEventData] = useState<IEventResponseModel>({
        id: '',
        title: '',
        description: '',
        location: '',
        image: '',
        eventDatesAndTickets: [{ id: '', start_time: new Date(), ticketTypes: [{ id: '', type: '', price: 0, total_tickets: 0 }] }],
        createdAt: new Date(),
        updatedAt: new Date()
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setEventData({ ...eventData, [name]: value });
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setEventData({ ...eventData, imageFile: e.target.files[0] });
        }
    };
    
	const handleEventDateBlur = (index: number, value: string) => {
		const newDate = new Date(value);

		if (Number.isNaN(newDate.getTime())) {
			return;
		}

		setEventData((prev) => ({
			...prev,
			eventDatesAndTickets: prev.eventDatesAndTickets.map((eventDate, i) =>
				i === index ? { ...eventDate, start_time: newDate } : eventDate
			),
		}));
	};

    const handleAddEventDate = () => {
        setEventData({
            ...eventData,
            eventDatesAndTickets: [...eventData.eventDatesAndTickets, { start_time: new Date(), ticketTypes: [{ type: '', price: 0, total_tickets: 0 }] }]
        });
    };

    const handleRemoveEventDate = (index: number) => {
        const updatedEventDates = eventData.eventDatesAndTickets.filter((_, i) => i !== index);
        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };


    const handleTicketTypeChange = (dateIndex: number, ticketIndex: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const updatedEventDates = eventData.eventDatesAndTickets.map((eventDate, i) => {
            if (i === dateIndex) {
                const updatedTicketTypes = eventDate.ticketTypes.map((ticket, j) =>
                    j === ticketIndex ? { ...ticket, [name]: value } : ticket
                );
                return { ...eventDate, ticketTypes: updatedTicketTypes };
            }
            return eventDate;
        });

        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };

    const handleAddTicketType = (dateIndex: number) => {
        const updatedEventDates = eventData.eventDatesAndTickets.map((eventDate, i) => {
            if (i === dateIndex) {
                return {
                    ...eventDate,
                    ticketTypes: [...eventDate.ticketTypes, { type: '', price: 0, total_tickets: 0 }]
                };
            }
            return eventDate;
        });

        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };

    const handleRemoveTicketType = (dateIndex: number, ticketIndex: number) => {
        const updatedEventDates = eventData.eventDatesAndTickets.map((eventDate, i) => {
            if (i === dateIndex) {
                const updatedTicketTypes = eventDate.ticketTypes.filter((_, j) => j !== ticketIndex);
                return { ...eventDate, ticketTypes: updatedTicketTypes };
            }
            return eventDate;
        });

        setEventData({ ...eventData, eventDatesAndTickets: updatedEventDates });
    };

    const handleUpdateEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setValidated(true);

        if (!eventData.title || !eventData.description || !eventData.location || !eventData.eventDatesAndTickets.length) {
            setErrorMessage("Все поля обязательны для заполнения");
            return;
        }

        for (let eventDate of eventData.eventDatesAndTickets) {
            for (let ticket of eventDate.ticketTypes) {
                if (!ticket.type || ticket.price <= 0 || ticket.total_tickets <= 0) {
                    setErrorMessage("Заполните корректно все поля для типов билетов");
                    return;
                }
            }
        }

        const formData = new FormData();

        if (eventData.title) {
            formData.append('title', eventData.title);
        }

        if (eventData.description) {
            formData.append('description', eventData.description);
        }

        if (eventData.location) {
            formData.append('location', eventData.location);
        }

        if (eventData.imageFile) {
            formData.append('image', eventData.imageFile);
        }
        if (eventData.eventDatesAndTickets) {
            formData.append('eventDatesAndTickets', JSON.stringify(eventData.eventDatesAndTickets))
        }

        updateEvent(eventId, formData)
            .then((response) => {
                if (response === '200') {
                    onEventEdited();
                    handleClose();
                    setErrorMessage('');
                    setValidated(false);
                } else {
                    setErrorMessage(response);
                }
            })
            .catch((error) => {
                setErrorMessage('Произошла ошибка при редактировании события. Пожалуйста, попробуйте снова.');
                console.error('Ошибка при редактировании события:', error);
            });
    };
	
	const formatDateTimeLocal = (date: Date | string) => {
        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    useEffect(() => {
        if (eventId) {
            getEventById(eventId)
                .then((data) => {
                    if (typeof data === 'string') {
                        setErrorMessage(data);
                    } else {
                        setEventData(data);
						setDateInputs(data.eventDatesAndTickets.map((x) => formatDateTimeLocal(x.start_time)));
                    }
                })
                .catch((error) => {
                    setErrorMessage('Ошибка при загрузке данных события');
                    console.error('Ошибка:', error);
                });
        }
    }, [eventId]);


    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Редактирование события</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                <Form className='addevent_form' noValidate validated={validated}>
                    <Form.Group>
                        <Form.Label>Название</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Введите название"
                            name="title"
                            value={eventData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Описание</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Введите описание"
                            name="description"
                            value={eventData.description}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Место проведения</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Введите местоположение"
                            name="location"
                            value={eventData.location}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Изображение</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                    </Form.Group>

                    <Form.Label>Даты проведения</Form.Label>
                    <Button variant="secondary" onClick={handleAddEventDate}>
                        Добавить дату
                    </Button>
                    {eventData.eventDatesAndTickets.map((eventDate, dateIndex) => (
                        <div key={dateIndex} className="event_unit">
                            <div>Мероприятие №{dateIndex + 1}</div>
                            <div className='event_date_list'>
                                <Form.Group controlId={`formEventDate${dateIndex}`}>
                                    <Form.Control
										type="datetime-local"
										value={dateInputs[dateIndex] || ''}
										onChange={(e) => {
											const value = e.target.value;
											setDateInputs((prev) => prev.map((item, i) => i === dateIndex ? value : item));
										}}
										onBlur={(e) => handleEventDateBlur(dateIndex, e.target.value)}
										required
									/>
                                </Form.Group>
                                <Button variant="danger" onClick={() => handleRemoveEventDate(dateIndex)}>
                                    <X />
                                </Button>
                            </div>
                            <div>
                                <Form.Label>Типы билетов</Form.Label>
                                <Button variant="secondary" onClick={() => handleAddTicketType(dateIndex)}>
                                    Добавить тип
                                </Button>
                            </div>
                            {eventDate.ticketTypes.map((ticketType, ticketIndex) => (
                                <div key={ticketIndex} className="ticket_type_container">
                                    <Button variant="danger" onClick={() => handleRemoveTicketType(dateIndex, ticketIndex)}>
                                        <X />
                                    </Button>
                                    <div className="ticket_type">
                                        <Form.Group>
                                            <Form.Label>Тип билета</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="type"
                                                value={ticketType.type}
                                                placeholder="Введите тип билета"
                                                onChange={(e) => handleTicketTypeChange(dateIndex, ticketIndex, e)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Цена</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="price"
                                                value={ticketType.price}
                                                placeholder="Введите цену"
                                                onChange={(e) => handleTicketTypeChange(dateIndex, ticketIndex, e)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Количество билетов</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="total_tickets"
                                                value={ticketType.total_tickets}
                                                placeholder="Введите количество билетов"
                                                onChange={(e) => handleTicketTypeChange(dateIndex, ticketIndex, e)}
                                                required
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Закрыть
                </Button>
                <Button variant="success" onClick={handleUpdateEvent}>
                    Отредактировать
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditEvent;
